"use client";
import { PlusCircle } from "@layer/icons";
import { Button } from "@layer/ui/components";
import { DataTable, PageDrawer } from "@layer/ui/patterns";
import { usePaginatedTableData } from "@layer/utils/hooks";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { useAvailableMerchants } from "@/api/merchant/queries/use-available-merchants";
import type { MerchantDTO } from "@/api/types/merchants.types";
import { getMerchantSelectColumns } from "@/features/campaigns/drawers/add-merchants-drawer/add-merchants-table-columns";
import { CreateMerchantForm } from "@/features/merchants/forms/create-merchant-form/create-merchant-form";

type AddClientMerchantsDrawerProps = {
	onSave: (merchants: MerchantDTO[]) => void;
};

const AddClientMerchantsDrawer = ({
	onSave,
}: AddClientMerchantsDrawerProps) => {
	const [open, setOpen] = useState(false);
	const [nestedOpen, setNestedOpen] = useState(false);
	const t = useTranslations("Features.merchants.addMerchants");

	const tA = useTranslations(
		"Features.campaigns.createCampaign.setReward.fields",
	);
	const [selectedMerchants, setSelectedMerchants] = useState<MerchantDTO[]>(
		[],
	);

	const [pageSize, setPageSize] = useState(25);
	const merchantsQuery = useAvailableMerchants({
		clientTenant: "mock-tenant",
		mock: true,
		pageSize,
	});
	const {
		isLoading,
		isPending,
		currentPageData,
		pageIndex,
		hasNextPage,
		handlePaginationChange,
	} = usePaginatedTableData<MerchantDTO>({
		queryResult: merchantsQuery,
		pageSize,
		setPageSize,
	});

	const columns = getMerchantSelectColumns({ t: tA });

	const handleSave = () => {
		onSave(selectedMerchants);
		setOpen(false);
	};

	const drawerTitle = (
		<div className="flex items-center justify-between w-full">
			<span>{t("title")}</span>
			<PageDrawer
				nested
				open={nestedOpen}
				onOpenChange={setNestedOpen}
				trigger={
					<Button
						variant="default"
						size="sm"
						className="flex items-center gap-2"
					>
						<span>{t("createNewMerchant.buttonLabel")}</span>
						<PlusCircle />
					</Button>
				}
				title={t("createNewMerchant.title")}
				content={
					<CreateMerchantForm onClose={() => setNestedOpen(false)} />
				}
			/>
		</div>
	);

	return (
		<PageDrawer
			nested
			open={open}
			onOpenChange={setOpen}
			title={drawerTitle}
			width="w-1/2"
			dismissible={false}
			trigger={
				<Button
					variant="default"
					size="default"
					className="flex items-center gap-3"
				>
					<span>{t("buttonLabel")}</span>
					<PlusCircle />
				</Button>
			}
			content={
				<div className="flex flex-col h-full">
					<div className="flex-1 min-h-0 p-4">
						<DataTable
							data={currentPageData}
							columns={columns}
							hasToolbar={true}
							isLoading={isLoading || isPending}
							pageIndex={pageIndex}
							pageSize={pageSize}
							hasNextPage={hasNextPage}
							onPaginationChange={handlePaginationChange}
							onSelectionChange={setSelectedMerchants}
						/>
					</div>
					<div className="border-t px-4 py-3 flex items-center justify-end gap-3 shrink-0">
						<Button
							variant="outline"
							onClick={() => setOpen(false)}
						>
							{"Cancel"}
						</Button>
						<Button onClick={handleSave}>
							{t("merchants.saveButton")}
						</Button>
					</div>
				</div>
			}
		/>
	);
};

export { AddClientMerchantsDrawer };
